import { AlertTriangle, PackageSearch } from "lucide-react";
import { Link } from "react-router";

import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { PUBLIC_ROUTES } from "@/constants/routes";
import { useFavoriteToggleHandler } from "@/features/engagement/hooks/useFavoriteToggleHandler";
import { useWishlist } from "@/features/engagement/hooks/useWishlist";
import { CategoryTile, CategoryTileSkeleton } from "@/features/marketplace/components/CategoryTile";
import { ProductCard, ProductCardSkeleton } from "@/features/marketplace/components/ProductCard";
import { useCategories } from "@/features/marketplace/hooks/useCategories";
import { useFeaturedProducts } from "@/features/marketplace/hooks/useFeaturedProducts";
import { ApiError } from "@/types/api";

/**
 * docs/ui/ui-architecture.md screen #1 — Home / Discovery feed. Category
 * row + product grid, each with its own loading (skeleton), error (retry),
 * and empty state — not just the happy path. With `server/` still empty,
 * the error path below is, in practice, the one this page actually
 * exercises right now, not an edge case to gloss over. (A first draft only
 * handled the product grid's error state and left categories silently
 * blank on failure — caught by actually loading the page, not by
 * typecheck/lint, since a missing branch isn't a type error.)
 *
 * The hero banner is a fixed brand-gradient, not a photo — there's no asset
 * pipeline or marketing imagery in this repo yet, and hotlinking a stock/
 * placeholder image from a third-party host isn't something to ship to
 * production. `text-display` (docs/ui/design-system.md §3.1: "Marketing
 * hero copy only (Home) — the rarest text style in the system") backs this
 * exact use. Both CTAs point at real routes rather than "#": Explore deals
 * to `/search`, How it works to `/help` (currently a placeholder page,
 * but a real route, not a fabricated link).
 */
export function HomePage() {
  const categoriesQuery = useCategories();
  const productsQuery = useFeaturedProducts();
  const wishlistQuery = useWishlist();
  const favoriteIds = new Set(wishlistQuery.data?.map((product) => product.id) ?? []);
  const handleFavoriteToggle = useFavoriteToggleHandler(favoriteIds);

  return (
    <>
      <div className="mx-auto max-w-[1280px] space-y-10 px-4 py-8">
        <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary-600 to-primary-900 px-8 py-16 text-white sm:px-12 sm:py-24">
          <div className="max-w-xl space-y-6">
            <h1 className="text-display">Elevate your lifestyle with BazaarX</h1>
            <p className="text-body-lg text-white/80">
              Discover a curated collection of pre-owned items from trusted sellers. Quality
              verified, secure transactions.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button type="button" size="lg" asChild>
                <Link to={PUBLIC_ROUTES.search}>Explore deals</Link>
              </Button>
              <Button
                type="button"
                size="lg"
                variant="outline"
                asChild
                className="border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white"
              >
                <Link to={PUBLIC_ROUTES.helpCenter}>How it works</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 text-foreground">Browse categories</h2>

          {categoriesQuery.isPending && (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
              {Array.from({ length: 6 }, (_, index) => (
                <CategoryTileSkeleton key={index} />
              ))}
            </div>
          )}

          {categoriesQuery.isError && (
            <p className="text-body-sm text-muted-foreground">
              Couldn&rsquo;t load categories.{" "}
              <button
                type="button"
                onClick={() => void categoriesQuery.refetch()}
                className="text-primary underline-offset-4 hover:underline"
              >
                Retry
              </button>
            </p>
          )}

          {categoriesQuery.data?.length === 0 && (
            <p className="text-body-sm text-muted-foreground">No categories yet.</p>
          )}

          {categoriesQuery.data && categoriesQuery.data.length > 0 && (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
              {categoriesQuery.data.map((category) => (
                <CategoryTile key={category.id} category={category} />
              ))}
            </div>
          )}
        </section>

        <section className="space-y-4">
          <h2 className="text-h2 text-foreground">Fresh listings</h2>

          {productsQuery.isPending && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 8 }, (_, index) => (
                <ProductCardSkeleton key={index} />
              ))}
            </div>
          )}

          {productsQuery.isError && (
            <EmptyState
              icon={AlertTriangle}
              heading="Couldn't load listings"
              description={
                productsQuery.error instanceof ApiError
                  ? productsQuery.error.message
                  : "Something went wrong. Please try again."
              }
              action={{ label: "Retry", onClick: () => void productsQuery.refetch() }}
            />
          )}

          {productsQuery.data?.length === 0 && (
            <EmptyState
              icon={PackageSearch}
              heading="No listings yet"
              description="Be the first to list something on BazaarX."
            />
          )}

          {productsQuery.data && productsQuery.data.length > 0 && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {productsQuery.data.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  isFavorite={favoriteIds.has(product.id)}
                  onFavoriteToggle={handleFavoriteToggle}
                />
              ))}
            </div>
          )}
        </section>
      </div>
      <Footer />
    </>
  );
}
